// ============================================================
// useWsEvent.ts — Hook générique pour s'abonner à un événement WS
// Usage : useWsEvent(WsEventType.REPORT_DONE, (event) => { ... })
// Cleanup automatique au démontage du composant
// ============================================================

import { useEffect, useRef } from 'react';
import { getSocket } from '../websocket/WebSocketClient';
import { WsEventType, WsPayload } from '../websocket/WsEvents';

export function useWsEvent<T>(
  eventType: WsEventType,
  callback:  (event: WsPayload<T>) => void,
): void {
  // Stocker le callback dans un ref pour éviter de re-souscrire à chaque render
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    let socket: ReturnType<typeof getSocket> | null = null;
    let handler: ((event: WsPayload<T>) => void) | null = null;

    /**
     * Attache le listener si le socket est dispo. Sinon retourne false —
     * un évènement window 'ws-ready' (déclenché par WebSocketClient.initSocket
     * au moment du connect) re-tentera l'attachement.
     *
     * Ce mécanisme corrige le piège suivant : si le composant qui appelle
     * useWsEvent monte AVANT initSocket() (ordre "child effect first" de React),
     * getSocket() throw au mount initial. Sans ce retry, le listener resterait
     * jamais attaché jusqu'au prochain remount — par ex. les notifs WS étaient
     * ignorées après un F5 alors que les fetch HTTP fonctionnaient.
     */
    const tryAttach = (): boolean => {
      try { socket = getSocket(); } catch { return false; }
      if (!socket) return false;
      handler = (event: WsPayload<T>) => callbackRef.current(event);
      socket.on(eventType, handler);
      return true;
    };

    let onReady: (() => void) | null = null;
    if (!tryAttach()) {
      onReady = () => {
        if (tryAttach() && onReady) {
          window.removeEventListener('ws-ready', onReady);
          onReady = null;
        }
      };
      window.addEventListener('ws-ready', onReady);
    }

    return () => {
      if (onReady) window.removeEventListener('ws-ready', onReady);
      if (socket && handler) socket.off(eventType, handler);
    };
  }, [eventType]);
}
